import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Package, ArrowRight } from "@phosphor-icons/react";

/**
 * Drilldown modal shown when the user clicks a bar on the Sales chart.
 * Lists every order created inside the clicked bucket window, with quick
 * status/amount info + a click-through to open the full OrderDetail dialog.
 */
export default function SalesOrdersDialog({ open, bucket, onClose, onOpenOrder }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !bucket) return;
    let cancel = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/analytics/sales-orders", {
          params: { from: bucket.from, to: bucket.to, project_type: bucket.project_type || undefined },
        });
        if (!cancel) setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancel) setBusy(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, bucket]);

  const openOrder = (id) => {
    onClose?.();
    // Give the dialog a tick to close before opening the next one
    setTimeout(() => onOpenOrder?.(id), 60);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-slate-900 border-slate-700 text-white rounded-sm max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="sales-orders-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// SALES BREAKDOWN</div>
          <DialogTitle className="font-heading text-2xl">
            {bucket?.dept_label ? `${bucket.dept_label} · ` : ""}{bucket?.date || bucket?.label || "Sales"}
          </DialogTitle>
          <div className="flex items-baseline gap-4 flex-wrap mt-1">
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL SALES</span>
              <div className="font-heading text-2xl font-black font-mono-num text-emerald-400">
                ₹{Number(data?.total_sales ?? bucket?.sales ?? 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">ORDERS</span>
              <div className="font-heading text-2xl font-black font-mono-num text-cyan-300">
                {data?.count ?? bucket?.orders ?? 0}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          {busy && <div className="text-slate-500 text-sm py-10 text-center">Loading…</div>}
          {!busy && data && data.orders.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center flex flex-col items-center gap-2">
              <Package size={26} className="text-slate-600" />
              No sales on this day.
            </div>
          )}
          {!busy && data && data.orders.length > 0 && (
            <div className="space-y-2">
              {data.orders.map((o) => (
                <button
                  key={o.id}
                  onClick={() => openOrder(o.id)}
                  className="w-full text-left surface-2 hover:bg-slate-800 transition-colors rounded-sm p-3 flex items-center gap-3 border border-slate-800 hover:border-slate-700"
                  data-testid={`sales-order-row-${o.order_number}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-num text-orange-400 font-bold">#{o.order_number}</span>
                      <StatusBadge status={o.status} />
                      {o.project_type === "gate_railing" && (
                        <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-cyan-500/15 border border-cyan-500/50 text-cyan-300">
                          GATE
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-slate-100 truncate">{o.customer_name}</div>
                    <div className="text-[10px] font-mono-num text-slate-500 mt-0.5">{o.mobile}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-slate-100 font-semibold">
                      ₹{Number(o.total_amount || 0).toLocaleString("en-IN")}
                    </div>
                    <div className="text-[10px] font-mono-num text-slate-500">
                      Paid ₹{Number(o.advance_paid || 0).toLocaleString("en-IN")}
                      {o.balance > 0 && (
                        <span className="text-orange-400"> · Due ₹{Number(o.balance).toLocaleString("en-IN")}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-slate-500 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
